import React from "react";
import { Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import HeadTittle from '../../../components/HeadTittle';
import ButtonAction from '../../../components/buttons/ButtonAction';
import { AssignIcon, IconBackLoginOptions } from '../../../components/buttons/IconButtons';
import routes from '../../../Routes/routes';
import DeleteModal from '../../Modals/DeleteModal';
import ConnectionStatus from '../DeliveredServices/SubComponents/ConnectionStatus';
import EventMonitoringMainApp from './Functionality/EventMonitoringMainApp';
import LayoutBoard from './SubComponents/LayoutBoard';
import Walkthrough from './SubComponents/Walkthrough';
import LayoutsModal from './Modal/LayoutsModal';
import AddPanelModal from './Modal/AddPanelModal';
import ExpandPanelModal from './Modal/ExpandPanelModal';
import { DELETE_TARGET, LAYOUTS_MODAL_TAB } from './Model/Model';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Event Monitoring: real time canvas of panels arranged in a layout. Every panel reads its events by
 * pages while it is scrolled, and the new ones come from the event monitoring topic (EventMonitoringWebSocket).
 */
const EventMonitoringMain = () => {
    const {
        t,
        canRead,
        canCreate,
        canUpdate,
        canDelete,
        isOnline,
        setModified,
        getPanelFeed,
        loadPanelEvents,
        loadAreaEvents,
        now,
        activeLayout,
        boardLayout,
        isWalkthroughMock,
        showLayoutsModal,
        setShowLayoutsModal,
        layoutsTab,
        setLayoutsTab,
        savedLayoutsModified,
        setSavedLayoutsModified,
        openLayoutsModal,
        showAddPanelModal,
        setShowAddPanelModal,
        panelToConfigure,
        setPanelToConfigure,
        panelToExpand,
        setPanelToExpand,
        deleteTarget,
        setDeleteTarget,
        deleteTargetModel,
        walkthroughStep,
        walkthroughSteps,
        handleCreateLayout,
        handleOpenLayout,
        handleRenameLayout,
        handleAddPanel,
        handleConfigurePanel,
        handleResizePanel,
        handleResizePanelEnd,
        handleConfirmDelete,
        closeWalkthrough,
        nextWalkthroughStep
    } = EventMonitoringMainApp();

    // The route carries no permission so the guard can open it, the admin needs the read one //
    if (!canRead) return <Navigate to={routes.forbidden} replace />;

    return (
        <>
            <HeadTittle name={'event_monitoring'} IconBack={IconBackLoginOptions} link={routes.login}>
                <Box className='d-flex gap-4 justify-content-end align-items-center mb-2'>
                    <ConnectionStatus
                        isOnline={isOnline}
                        setModified={setModified}
                        showStatus={true}
                    />
                    <ButtonAction
                        id='btn_emm_layouts'
                        name='layouts'
                        className='btn-icon-add'
                        Icon={AssignIcon}
                        splitName={false}
                        onClick={() => openLayoutsModal(LAYOUTS_MODAL_TAB.CREATE.ID)}
                    />
                </Box>
            </HeadTittle>
            <div className='flex-grow-1 overflow-hidden mb-60px'>
                <LayoutBoard
                    layout={boardLayout}
                    getPanelFeed={getPanelFeed}
                    onLoadMore={loadPanelEvents}
                    onLoadAreaEvents={loadAreaEvents}
                    now={now}
                    onCreateLayout={() => openLayoutsModal(LAYOUTS_MODAL_TAB.CREATE.ID)}
                    onAddPanel={() => setShowAddPanelModal(true)}
                    onConfigurePanel={setPanelToConfigure}
                    onExpandPanel={setPanelToExpand}
                    // The example panel of the walkthrough is not stored, so it can not be deleted or resized //
                    onDeletePanel={(panel) => !isWalkthroughMock && setDeleteTarget({ type: DELETE_TARGET.PANEL.ID, id: panel.id })}
                    onResizePanel={(...args) => !isWalkthroughMock && handleResizePanel(...args)}
                    onResizePanelEnd={() => !isWalkthroughMock && handleResizePanelEnd()}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                />
            </div>

            {showLayoutsModal &&
                <LayoutsModal
                    show={showLayoutsModal}
                    onHide={() => setShowLayoutsModal(false)}
                    activeTab={layoutsTab}
                    setActiveTab={setLayoutsTab}
                    activeLayoutId={activeLayout?.id}
                    savedLayoutsModified={savedLayoutsModified}
                    setSavedLayoutsModified={setSavedLayoutsModified}
                    display={deleteTarget ? 'd-none' : ''}
                    onCreate={handleCreateLayout}
                    onOpen={handleOpenLayout}
                    onRename={handleRenameLayout}
                    onDelete={(layout) => setDeleteTarget({ type: DELETE_TARGET.LAYOUT.ID, id: layout.id })}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                />
            }

            {showAddPanelModal &&
                <AddPanelModal
                    show={showAddPanelModal}
                    onHide={() => setShowAddPanelModal(false)}
                    onConfirm={handleAddPanel}
                />
            }

            {panelToConfigure &&
                <AddPanelModal
                    show={!!panelToConfigure}
                    panel={panelToConfigure}
                    onHide={() => setPanelToConfigure(null)}
                    onConfirm={handleConfigurePanel}
                />
            }

            {panelToExpand &&
                <ExpandPanelModal
                    show={!!panelToExpand}
                    panel={panelToExpand}
                    feed={getPanelFeed(panelToExpand)}
                    onLoadMore={loadPanelEvents}
                    now={now}
                    display={panelToConfigure ? 'd-none' : ''}
                    onHide={() => setPanelToExpand(null)}
                    onConfigure={setPanelToConfigure}
                    canUpdate={canUpdate}
                />
            }

            <DeleteModal
                show={!!deleteTarget}
                onHide={() => setDeleteTarget(null)}
                onClickFn={handleConfirmDelete}
                title_modal={deleteTargetModel.TITLE}
                data={deleteTarget ? [deleteTarget.id] : []}
                element_modal={deleteTargetModel.ELEMENT}
            />

            {walkthroughStep !== null &&
                <Walkthrough
                    step={walkthroughStep}
                    steps={walkthroughSteps}
                    onNext={nextWalkthroughStep}
                    onClose={closeWalkthrough}
                    t={t}
                />
            }
        </>
    );
};

export default EventMonitoringMain;
